import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter, useHistory, useLocation } from 'react-router-dom';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';

import { AngularServicesContext } from 'react-app';
import t from 'react-translate';

import { AnalyticsDashBoardType, AnalyticsPageType } from 'redux/schemas/app/analytics';
import { fetchCohorts, fetchCohortsQuestionSet } from 'redux/actions/analytics';
import { useAppDispatch } from 'redux/store';
import { showPrePostTab } from 'redux/selectors/analytics';

import { gray6, warning } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';

import NvIcon from 'shared/components/nv-icon';
import { resetQrveyValues } from 'analytics_dashboard/hooks/use-initialize-qrvey';
import QuizAndSurveyAnalyticsTab from './quiz-survey-analytics';
import QrveyWrapper from './qrvey-wrapper';
import AnalyticsDashboard, { AnalyticsContext } from './analytics-dashboard';
import QrveyPanelsWrapper from './qrvey-panels-wrapper';
import PrePostSurvey from './pre-post-survey';
import AnalyticsTabHeader from './analytics-tab-header';

enum CohortTabs {
  OVERVIEW = 0,
  QUIZ_SURVEY = 1,
  PRE_POST = 2,
}

const styles = css`
  .tab-container {
    position: sticky;
    top: 0;
    z-index: 2;
    border-bottom: solid 1px ${gray6};
    height: 60px;
  }
  .bottom-border-clickable {
    border-bottom: solid ${quarterSpacing}px ${warning}
  }
  .quiz-survey-dashboard {
    max-width: 1030px;
  }
  .question-title {
    max-width: 800px;
  }
`;

export const getBaseUrl = (catalogId) => (`/#!/courses/${catalogId}/cohort-analytics`);

// Helper function to determine tab from URL
const getTabFromLocation = (hash: string, questionSetId?: number): CohortTabs => {
  if (hash?.endsWith('/pre-post-survey')) {
    return CohortTabs.PRE_POST;
  }
  if (hash?.includes('/quiz-and-survey/') || questionSetId) {
    return CohortTabs.QUIZ_SURVEY;
  }
  return CohortTabs.OVERVIEW;
};

const CohortAnalytics: React.FC = () => {
  const { $state } = useContext(AngularServicesContext);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { catalogId } = $state?.params || {};
  const initialQuestionSetId = $state?.params?.questionId || null;

  const [questionSetId, setQuestionSetId] = useState<number | null>(initialQuestionSetId);
  const [activeTab, setActiveTab] = useState<CohortTabs>(() => getTabFromLocation(location?.hash, initialQuestionSetId));

  const institutionId = useSelector((state) => state.app.currentInstitutionId);
  const cohorts = useSelector(state => state.app.analytics.cohorts);
  const showPrePostSurveyTab = useSelector((state) => showPrePostTab(state, AnalyticsPageType.COHORT_ANALYTICS));
  const prePostSurveyConfigurationId = useSelector(state => state.app.analytics.currentPrePostConfigurationId);

  const BASE_URL = getBaseUrl(catalogId);

  const onSelectTab = (tab: CohortTabs, id?: number) => {
    resetQrveyValues();
    setActiveTab(tab);

    if (tab === CohortTabs.QUIZ_SURVEY && id) {
      setQuestionSetId(id);
      history.push(`${BASE_URL}/quiz-and-survey/${id}`);
    } else if (tab === CohortTabs.PRE_POST) {
      setQuestionSetId(null);
      window.location.href = `${BASE_URL}/pre-post-survey`;
    } else {
      setQuestionSetId(null);
      window.location.href = BASE_URL;
    }
  };

  // Fetch survey questions to determine whether to show pre-post survey tab
  useEffect(() => {
    dispatch(fetchCohortsQuestionSet({
      institutionId,
      catalogId,
      filterByType: 'Survey',
    }));
  }, [catalogId, dispatch, institutionId]);

  // Fetch cohorts when questionSetId changes
  useEffect(() => {
    if (questionSetId) {
      dispatch(fetchCohorts({ catalogId, institutionId, questionSetId }));
    }
  }, [dispatch, institutionId, catalogId, questionSetId]);

  // Sync state from URL params (handles browser back/forward and direct URL access)
  useEffect(() => {
    const paramQuestionId = $state.params?.questionId;
    if (paramQuestionId !== questionSetId) {
      setQuestionSetId(paramQuestionId || null);
    }
  }, [$state.params?.questionId]);

  // Sync activeTab from location hash
  useEffect(() => {
    // To set tab when user directly accesses URL or uses browser back/forward buttons
    const newTab = getTabFromLocation(location?.hash, questionSetId);
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  // ActiveTab cannot be included in dependencies to avoid infinite loop
  // source of truth is the location hash
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.hash, questionSetId]);

  return (
    <div css={styles}>
      <div className='d-flex justify-content-between bg-white px-4 tab-container'>
        <div className='d-flex pr-2'>
          <AnalyticsTabHeader
            isActive={activeTab === CohortTabs.OVERVIEW}
            onClick={() => onSelectTab(CohortTabs.OVERVIEW)}
          >
            {t.COURSE_ANALYTICS.OVERVIEW()}
          </AnalyticsTabHeader>
          <QuizAndSurveyAnalyticsTab
            selectedId={questionSetId}
            onSelect={(id) => onSelectTab(CohortTabs.QUIZ_SURVEY, id)}
            analyticsPageType={AnalyticsPageType.COHORT_ANALYTICS}
          />
          {showPrePostSurveyTab && (
            <AnalyticsTabHeader
              isActive={activeTab === CohortTabs.PRE_POST}
              onClick={() => onSelectTab(CohortTabs.PRE_POST)}
            >
              {t.COURSE_ANALYTICS.PRE_POST.TAB_HEADER()}
            </AnalyticsTabHeader>
          )}
        </div>
        <div className='d-flex align-items-center'>
          <a
            href={$state?.href('course-analytics', { catalogId })}
            className='d-flex text-primary font-weight-bold '
            aria-labelledby='course-analytics-link'
          >
            <NvIcon icon='course' size='smallest' />
            <span className='ml-1' id='course-analytics-link'>
              {t.INSTITUTIONS.COURSE_ANALYTICS.HEADER()}
            </span>
          </a>
        </div>
      </div>
      {activeTab === CohortTabs.OVERVIEW && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COHORTS}
          configKeyName='config_cohorts'
          pageIdKey='cohorts'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {activeTab === CohortTabs.QUIZ_SURVEY && questionSetId && (
        <div className='d-flex justify-content-center quiz-survey-dashboard mx-auto'>
          <AnalyticsDashboard
            dashboardType={AnalyticsDashBoardType.COHORT_QUESTION_SET}
            configKeyName={`config_cohorts_question_set_${questionSetId}`}
            pageIdKey='cohorts_question_set'
            questionSetId={questionSetId}
            cohorts={cohorts}
            analyticsPageType={AnalyticsPageType.COHORT_ANALYTICS}
          >
            <QrveyPanelsWrapper />
          </AnalyticsDashboard>
        </div>
      )}
      {activeTab === CohortTabs.PRE_POST && showPrePostSurveyTab && (
        <AnalyticsContext.Provider
          value={{
            dashboardType: null,
            analyticsPageType: AnalyticsPageType.COHORT_ANALYTICS,
            catalogId,
            institutionId,
            prePostSurveyConfigurationId,
          }}
        >
          <PrePostSurvey />
        </AnalyticsContext.Provider>
      )}
    </div>
  );
};

export default CohortAnalytics;

export const CohortAnalyticsRoute = () => <BrowserRouter forceRefresh><CohortAnalytics /></BrowserRouter>;
